/**
 * Region Switcher Modal
 * Browse, search, and switch between regions
 * Create new regions via modal
 */

import React, { useState, useMemo } from 'react';
import { RegionHeader, RegionId } from '@maco144/shared';
import { CANVAS_THEME } from './canvas-theme';
import { RegionCreationForm } from './region-creation-form';

export interface RegionSwitcherModalProps {
  isOpen: boolean;
  onClose: () => void;
  regions: RegionHeader[];
  currentRegionId: RegionId | null;
  homeRegionId: RegionId | null;
  onSwitchRegion: (regionId: RegionId) => void;
  onRegionCreated?: (regionId: RegionId) => void;
}

type ModalTab = 'browse' | 'create';

/**
 * Region Switcher Modal
 * Features:
 * - Browse all available regions
 * - Search by name or type
 * - Filter by region type
 * - Join/switch regions
 * - Create new regions
 * - Show home region with special indicator
 */
export const RegionSwitcherModal: React.FC<RegionSwitcherModalProps> = ({
  isOpen,
  onClose,
  regions,
  currentRegionId,
  homeRegionId,
  onSwitchRegion,
  onRegionCreated,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ModalTab>('browse');

  if (!isOpen) return null;

  // Get unique region types
  const regionTypes = Array.from(new Set(regions.map((r) => r.type)));

  // Filter regions by search query and type
  const filteredRegions = useMemo(() => {
    return regions.filter((region) => {
      const matchesSearch =
        !searchQuery || (region.name || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = !typeFilter || region.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [regions, searchQuery, typeFilter]);

  // Sort: home region first, then by name
  const sortedRegions = [...filteredRegions].sort((a, b) => {
    if (a.id === homeRegionId) return -1;
    if (b.id === homeRegionId) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const handleRegionCreated = (regionId: RegionId) => {
    onRegionCreated?.(regionId);
    setActiveTab('browse');
  };

  return (
    <>
      {/* Modal Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: CANVAS_THEME.zIndex.modal - 1,
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          background: CANVAS_THEME.panelBackground,
          backdropFilter: CANVAS_THEME.panelBackdrop,
          border: CANVAS_THEME.panelBorder,
          borderRadius: CANVAS_THEME.panelBorderRadius,
          boxShadow: CANVAS_THEME.panelShadow,
          zIndex: CANVAS_THEME.zIndex.modal,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: CANVAS_THEME.textPrimary,
            }}
          >
            Regions
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: CANVAS_THEME.textSecondary,
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: `all ${CANVAS_THEME.transitionDuration}`,
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.background = 'none';
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            padding: '16px 20px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
            background: 'rgba(255, 255, 255, 0.3)',
          }}
        >
          {['browse', 'create'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as ModalTab)}
              style={{
                padding: '8px 16px',
                background: activeTab === tab ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
                border:
                  activeTab === tab
                    ? '1px solid rgba(102, 126, 234, 0.5)'
                    : '1px solid transparent',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeTab === tab ? '600' : '500',
                color: activeTab === tab ? CANVAS_THEME.accentColor : CANVAS_THEME.textSecondary,
                transition: `all ${CANVAS_THEME.transitionDuration}`,
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab) {
                  (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.03)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab) {
                  (e.target as HTMLButtonElement).style.background = 'transparent';
                }
              }}
            >
              {tab === 'browse' ? '📋 Browse' : '✨ Create'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '20px',
          }}
        >
          {activeTab === 'browse' ? (
            <>
              {/* Search */}
              <input
                type="text"
                placeholder="Search regions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  marginBottom: '16px',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  background: 'rgba(255, 255, 255, 0.5)',
                  color: CANVAS_THEME.textPrimary,
                  boxSizing: 'border-box',
                }}
              />

              {/* Type Filter */}
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: CANVAS_THEME.textSecondary,
                    marginBottom: '8px',
                  }}
                >
                  Filter by Type:
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <button
                    onClick={() => setTypeFilter(null)}
                    style={{
                      padding: '6px 12px',
                      background: !typeFilter ? 'rgba(102, 126, 234, 0.2)' : 'rgba(0, 0, 0, 0.03)',
                      border:
                        '1px solid ' +
                        (!typeFilter ? 'rgba(102, 126, 234, 0.5)' : 'rgba(0, 0, 0, 0.1)'),
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      color: !typeFilter ? CANVAS_THEME.accentColor : CANVAS_THEME.textSecondary,
                      transition: `all ${CANVAS_THEME.transitionDuration}`,
                    }}
                  >
                    All
                  </button>
                  {regionTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      style={{
                        padding: '6px 12px',
                        background:
                          typeFilter === type ? 'rgba(102, 126, 234, 0.2)' : 'rgba(0, 0, 0, 0.03)',
                        border:
                          '1px solid ' +
                          (typeFilter === type ? 'rgba(102, 126, 234, 0.5)' : 'rgba(0, 0, 0, 0.1)'),
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        color:
                          typeFilter === type
                            ? CANVAS_THEME.accentColor
                            : CANVAS_THEME.textSecondary,
                        transition: `all ${CANVAS_THEME.transitionDuration}`,
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Regions List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sortedRegions.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: CANVAS_THEME.textSecondary,
                    }}
                  >
                    No regions found
                  </div>
                ) : (
                  sortedRegions.map((region) => {
                    const isHome = region.id === homeRegionId;
                    const isCurrent = region.id === currentRegionId;
                    return (
                      <div
                        key={region.id}
                        style={{
                          padding: '14px',
                          background: isCurrent
                            ? 'rgba(102, 126, 234, 0.1)'
                            : 'rgba(255, 255, 255, 0.3)',
                          border: isCurrent
                            ? '1px solid rgba(102, 126, 234, 0.3)'
                            : '1px solid rgba(0, 0, 0, 0.05)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: `all ${CANVAS_THEME.transitionDuration}`,
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          const el = e.currentTarget as HTMLDivElement;
                          if (!isCurrent) {
                            el.style.background = 'rgba(102, 126, 234, 0.08)';
                            el.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          const el = e.currentTarget as HTMLDivElement;
                          if (!isCurrent) {
                            el.style.background = 'rgba(255, 255, 255, 0.3)';
                            el.style.borderColor = 'rgba(0, 0, 0, 0.05)';
                          }
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '4px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: CANVAS_THEME.textPrimary,
                              }}
                            >
                              {region.name || `#${region.id.slice(0, 8)}`}
                            </span>
                            {isHome && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  background: 'rgba(102, 126, 234, 0.2)',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  color: CANVAS_THEME.accentColor,
                                }}
                              >
                                HOME
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: CANVAS_THEME.textSecondary }}>
                            {region.type}
                            {region.public && ' • Public'}
                          </div>
                        </div>
                        <button
                          onClick={() => onSwitchRegion(region.id)}
                          style={{
                            padding: '8px 16px',
                            background: isCurrent
                              ? 'rgba(102, 126, 234, 0.3)'
                              : 'rgba(102, 126, 234, 0.15)',
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: CANVAS_THEME.accentColor,
                            transition: `all ${CANVAS_THEME.transitionDuration}`,
                            marginLeft: '12px',
                            whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={(e) => {
                            (e.target as HTMLButtonElement).style.background =
                              'rgba(102, 126, 234, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            (e.target as HTMLButtonElement).style.background = isCurrent
                              ? 'rgba(102, 126, 234, 0.3)'
                              : 'rgba(102, 126, 234, 0.15)';
                          }}
                        >
                          {isCurrent ? '✓ Current' : 'Join'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <RegionCreationForm
              onSuccess={handleRegionCreated}
              onCancel={() => setActiveTab('browse')}
            />
          )}
        </div>
      </div>
    </>
  );
};
